import type { ActorDTO, RoutineDTO, RoutineRunDTO } from "@roomate/core";

/** The list projection of GET /api/routines: the last run carries no report, summary, window or proposals. */
export type RoutineListDTO = Omit<RoutineDTO, "lastRun"> & { lastRun: Pick<RoutineRunDTO, "id" | "status" | "costUsd" | "createdAt"> | null };

/** Q14 (docs/specs/routines.md): only admins create, edit, run, pause or resume, the same rule as the hub's assertRoutineAdmin. The API enforces it too; this only hides the buttons. */
export function canManageRoutines(me: Pick<ActorDTO, "access"> | undefined): boolean {
  return !!me && me.access === "admin";
}

/** Run duration in ms by the agent run's id (RunDTO.id), for runs that have finished. A routine run points at it with `runId`. */
export function durationsByRunId(runs: readonly { id: string; startedAt: number; finishedAt: number | null }[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const r of runs) if (r.finishedAt) out.set(r.id, r.finishedAt - r.startedAt);
  return out;
}

const UNFINISHED = new Set<string>(["starting", "running"]);

/**
 * The cost of the last finished run, for the cap meter. A run that is still starting or running has not
 * spent its cost yet, so it is skipped. Reads the full history when it has loaded and falls back to the
 * routine's own `lastRun` while that query loads or has failed. `history` must be newest first. Stopped,
 * superseded and budget_exceeded runs count as finished: they spent real cost. Null when no run has finished.
 */
export function lastFinishedCost(
  history: readonly Pick<RoutineRunDTO, "status" | "costUsd">[],
  lastRun: Pick<RoutineRunDTO, "status" | "costUsd"> | null | undefined,
): number | null {
  const done = history.find((r) => !UNFINISHED.has(r.status)) ?? (lastRun && !UNFINISHED.has(lastRun.status) ? lastRun : undefined);
  return done ? done.costUsd : null;
}

/** "every day 00:00 Asia/Jerusalem" (docs/specs/routines.md §3.1). */
export function scheduleWords(r: Pick<RoutineDTO, "timeOfDay" | "timezone">): string {
  return `every day ${r.timeOfDay} ${r.timezone}`;
}

export const STATUS_LABEL: Record<RoutineDTO["status"], string> = {
  active: "Active",
  paused: "Paused",
  paused_failing: "Paused: failing",
};

/** `paused_failing` first (it needs a person), then by `nextDueAt`. The hub already sorts; this keeps the order after a local patch. */
export function sortRoutines<T extends Pick<RoutineDTO, "status" | "nextDueAt">>(routines: readonly T[]): T[] {
  const rank = (r: T) => (r.status === "paused_failing" ? 0 : 1);
  return [...routines].sort((a, b) => rank(a) - rank(b) || a.nextDueAt - b.nextDueAt);
}

/** Run history, newest first. */
export function newestFirst<T extends Pick<RoutineRunDTO, "createdAt">>(runs: readonly T[]): T[] {
  return [...runs].sort((a, b) => b.createdAt - a.createdAt);
}

/** A run's number counts from the oldest, so it stays the same as new runs arrive. */
export function runNumbers(runs: readonly Pick<RoutineRunDTO, "id" | "createdAt">[]): Map<string, number> {
  const oldestFirst = [...runs].sort((a, b) => a.createdAt - b.createdAt);
  return new Map(oldestFirst.map((r, i) => [r.id, i + 1]));
}

/** A time in the routine's own zone, e.g. "27 Oct, 00:00". Falls back to the viewer's zone if the name is unknown. */
export function formatInZone(ts: number, timezone: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false };
  try {
    return new Date(ts).toLocaleString([], { ...opts, timeZone: timezone });
  } catch {
    return new Date(ts).toLocaleString([], opts);
  }
}

export function formatLocal(ts: number): string {
  return new Date(ts).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Cost per run against its cap, as a 0 to 1 fraction clamped for a bar. */
export function capFraction(costUsd: number, maxBudgetUsd: number): number {
  if (!(maxBudgetUsd > 0)) return 0;
  return Math.min(1, Math.max(0, costUsd / maxBudgetUsd));
}

export const RUN_STATUS_TONE: Record<string, "live" | "danger" | "neutral" | "signal"> = {
  succeeded: "live",
  failed: "danger",
  budget_exceeded: "danger",
  starting: "signal",
  running: "signal",
};
